import { apiFetch } from '@/lib/api/http';

export type SeatingStatus = 'draft' | 'confirmed' | 'published';

export const SEATING_STATUS_LABELS: Record<SeatingStatus, string> = {
  draft: '下書き',
  confirmed: '確定',
  published: '公開済み',
};

export interface SeatingAssignment {
  id: string;
  member_id: string;
  member_name: string | null;
  table_no: number;
  seat_no: number | null;
}

export interface SeatingVersion {
  id: string;
  event_id: string;
  version: number;
  status: SeatingStatus;
  published_at: string | null;
  warnings: Record<string, unknown>[];
  override_reason: string | null;
  assignments: SeatingAssignment[];
}

export function fetchCurrentSeating(eventId: string): Promise<SeatingVersion | null> {
  return apiFetch<SeatingVersion | null>(`/events/${eventId}/seating`);
}

export function fetchSeatingVersions(eventId: string): Promise<SeatingVersion[]> {
  return apiFetch<SeatingVersion[]>(`/events/${eventId}/seating/versions`);
}

export function confirmSeatingVersion(
  eventId: string,
  versionId: string,
  overrideReason?: string | null,
): Promise<SeatingVersion> {
  return apiFetch<SeatingVersion>(`/events/${eventId}/seating/versions/${versionId}/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ override_reason: overrideReason ?? null }),
  });
}

export function publishSeatingVersion(eventId: string, versionId: string): Promise<SeatingVersion> {
  return apiFetch<SeatingVersion>(`/events/${eventId}/seating/versions/${versionId}/publish`, {
    method: 'POST',
  });
}

export function updateSeatingAssignment(
  assignmentId: string,
  payload: { table_no: number; seat_no?: number | null },
): Promise<SeatingAssignment> {
  return apiFetch<SeatingAssignment>(`/seating-assignments/${assignmentId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

// --- NGルール（implementation-spec §9） --------------------------------------

export type NgRuleSource = 'office' | 'success_request';
export type NgRuleStatus = 'requested' | 'active' | 'rejected' | 'archived';

export const NG_RULE_STATUS_LABELS: Record<NgRuleStatus, string> = {
  requested: '申請中',
  active: '有効',
  rejected: '却下',
  archived: '無効化',
};

export interface NgRule {
  id: string;
  member_a_id: string;
  member_b_id: string;
  member_a_name: string | null;
  member_b_name: string | null;
  reason_category: string;
  confidential_reason: string | null;
  source: NgRuleSource;
  status: NgRuleStatus;
}

export function fetchNgRules(): Promise<NgRule[]> {
  return apiFetch<NgRule[]>('/ng-rules');
}

export function createNgRule(payload: {
  member_a_id: string;
  member_b_id: string;
  reason_category: string;
  confidential_reason?: string | null;
}): Promise<NgRule> {
  return apiFetch<NgRule>('/ng-rules', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function setNgRuleStatus(ngRuleId: string, status: NgRuleStatus): Promise<NgRule> {
  return apiFetch<NgRule>(`/ng-rules/${ngRuleId}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
}
